import { useMe } from '@/features/authentication/_hooks/me';
import {
  type TableColumnsSettingModel,
  TableColumnsSettingStorageModel,
  TableNestSettingStorageModel,
} from '@/features/project/_models/local-storage';
import type { TableNestSettingModel } from '@/features/project/_models/project-filter';
import { MAXIMUM_SEARCH_HISTORY_LIMIT } from '@/features/project/_utils/constants';
import { SEARCH_HISTORY_KEY } from '@/features/project/_utils/storage-key';
import { generateHistoryDateTime } from '@/features/search/_utils/local-storage';
import { isEqual } from 'lodash-es';
import useLocalStorageState from 'use-local-storage-state';
import type { z } from 'zod';
import type { ProjectsPageSearchQueryStates } from './search-query-states';

export const useTableNestSettingStorage = (userId: string, key: string) => {
  const [tableNestSettingStorage, setTableNestSettingStorage] =
    useLocalStorageState<z.infer<typeof TableNestSettingStorageModel>>(key);

  const parsedTableNestSetting = TableNestSettingStorageModel.safeParse(
    tableNestSettingStorage,
  );
  const nestSetting = parsedTableNestSetting.success
    ? parsedTableNestSetting.data
    : null;

  const tableNestSetting = nestSetting?.[userId];

  return {
    tableNestSettingStorage: tableNestSetting,
    setTableNestSettingStorage: (
      values: z.infer<typeof TableNestSettingModel>,
    ) => {
      setTableNestSettingStorage((localStorageValue) => ({
        ...localStorageValue,
        [userId]: values,
      }));
    },
  };
};

export const useTableColumnsSettingStorage = (userId: string, key: string) => {
  const [tableColumnsSettingStorage, setTableColumnsSettingStorage] =
    useLocalStorageState<z.infer<typeof TableColumnsSettingStorageModel>>(key);

  const parsedTableColumnsSetting = TableColumnsSettingStorageModel.safeParse(
    tableColumnsSettingStorage,
  );

  const columnsSetting = parsedTableColumnsSetting.success
    ? parsedTableColumnsSetting.data
    : null;

  const tableColumnsSetting = columnsSetting?.[userId];

  return {
    tableColumnsSettingStorage: tableColumnsSetting,
    setTableColumnsSettingStorage: (
      values: z.infer<typeof TableColumnsSettingModel>,
    ) => {
      setTableColumnsSettingStorage((localStorageValue) => ({
        ...localStorageValue,
        [userId]: values,
      }));
    },
  };
};

type ProjectSearchHistoryStorageType = {
  [key: string]: {
    datetime: string;
    filterConditions: ProjectsPageSearchQueryStates;
    searchHistoryLabelsMap:
      | {
          label: string;
          value: string | number;
        }[]
      | undefined;
  }[];
};

export const useSearchHistory = () => {
  const me = useMe();
  const userId = me.data?.id ?? '';
  const key = SEARCH_HISTORY_KEY;

  const [searchHistoryStorage, setSearchHistoryStorage] =
    useLocalStorageState<ProjectSearchHistoryStorageType>(key);

  const searchHistoryData = searchHistoryStorage?.[userId];
  const lastSearched = searchHistoryData?.at(0)?.filterConditions;

  const lastSearchedQueryParams = new URLSearchParams(
    lastSearched
      ? Object.fromEntries(
          Object.entries(lastSearched).map(([key, value]) => {
            if (key === 'formulaConditions') {
              return [key, JSON.stringify(value)];
            }
            return [key, String(value)];
          }),
        )
      : undefined,
  );

  return {
    lastSearched,
    lastSearchedQueryParams,
    searchHistoryData,
    setSearchHistoryStorage: (
      filterConditions: ProjectsPageSearchQueryStates,
      searchHistoryLabelsMap:
        | {
            label: string;
            value: string | number;
          }[]
        | undefined,
    ) => {
      setSearchHistoryStorage((prevValue) => {
        const userHistoryByDate = [...(prevValue?.[userId] ?? [])].sort(
          (a, b) =>
            new Date(b.datetime).getTime() - new Date(a.datetime).getTime(),
        );

        // 最新の履歴の検索条件
        const latestFilterConditions =
          userHistoryByDate.at(0)?.filterConditions;

        // 最新の履歴の検索条件と今回の検索条件が同じ場合は最新の履歴のdatetimeを更新
        if (isEqual(latestFilterConditions, filterConditions)) {
          const [latestValue, ...rest] = userHistoryByDate;
          return {
            ...prevValue,
            [userId]: [
              ...(latestValue
                ? [
                    {
                      ...latestValue,
                      datetime: generateHistoryDateTime(),
                    },
                  ]
                : []),
              ...rest,
            ],
          };
        }
        return {
          ...prevValue,
          [userId]: [
            {
              filterConditions,
              datetime: generateHistoryDateTime(),
              searchHistoryLabelsMap,
            },
            ...userHistoryByDate,
          ].filter((_, index) => index < MAXIMUM_SEARCH_HISTORY_LIMIT),
        };
      });
    },
  };
};
